import {inject, Injectable, Injector, isDevMode, runInInjectionContext}                                         from '@angular/core';
import {addDoc, collection, deleteDoc, doc, Firestore, onSnapshot, orderBy, query, Timestamp, updateDoc, where} from '@angular/fire/firestore';
import {Functions, httpsCallable}                                                                               from '@angular/fire/functions';
import {CrudRecordType, FirebasePaths, GlobalRecordIntf, OuraRecordOnCallType, OutgoingDataIntf}                from '@djsharkk/logr-types';
import {DocumentData, OrderByDirection}                                                                         from '@firebase/firestore';
import {Observable}                                                                                             from 'rxjs';
import {UiDialogsService}                                                                                       from '../../logr-ui/utils/ui-dialogs.service';
import {UiNotificationsService}                                                                                 from '../../logr-ui/utils/ui-notifications.service';
import {DataFilterService}                                                                                      from '../data/data-filter.service';


@Injectable({
				providedIn: 'root'
			})
export class FirebaseToolsService {
	injector = inject(Injector)

	svcDataFilters = inject(DataFilterService);
	private svcUiNotifications = inject(UiNotificationsService);
	private svcUiDialogs = inject(UiDialogsService);

	firebaseEndpoint = isDevMode() ? 'http://127.0.0.1:5001/logr-pwa-2024/us-central1' : 'https://us-central1-logr-pwa-2024.cloudfunctions.net'


	recFetch<T>(
		path: FirebasePaths,
		dateKey?: keyof T,
		dateStart: Date = this.svcDataFilters.dateStart,
		dateEnd: Date           = this.svcDataFilters.dateEnd,
		order: OrderByDirection = 'asc'
	): Observable<T[]> {
		const firestore = inject(Firestore)
		const collectionRef = collection(firestore, path);

		const q =
				  dateKey
				  ? query(
					  collectionRef,
					  where(dateKey as string, '>=', dateStart),
					  where(dateKey as string, '<=', dateEnd),
					  orderBy(dateKey as string, order)
				  )
				  : query(collectionRef);

		return new Observable<T[]>(subscriber => {
			const unsubscribe = onSnapshot(q, snapshot => {
				const results = snapshot.docs.map(doc => ({
					...doc.data() as T,
					id: doc.id
				}));
				subscriber.next(results);
			}, err => subscriber.error(err));

			// Return clean-up function which is invoked on unsubscription
			return () => unsubscribe();
		});
	}


	getOuraApi<T>(func: OuraRecordOnCallType, s = new Date(), e = new Date()) {
		const functions = inject(Functions)
		const getData = httpsCallable<OutgoingDataIntf, T>(functions, func);
		return getData({
						   dateStart: s,
						   dateEnd  : e
					   });
	}


	// New method for creating a document.
	async recCreate<T>(path: FirebasePaths, data: T) {
		await runInInjectionContext(this.injector, async () => {
			const firestore = inject(Firestore);
			const collectionRef = collection(firestore, path);
			await addDoc(collectionRef, data as Object);
			await this.svcUiNotifications.showNotification('Created');
		})
	}

	// New method for updating a document.
	async recUpdate<T extends DocumentData>(path: FirebasePaths, docId: string, data: T) {
		await runInInjectionContext(this.injector, async () => {
			const firestore = inject(Firestore);
			const docRef = doc(firestore, path, docId);
			await updateDoc(docRef, data);
			await this.svcUiNotifications.showNotification('Updated');
		})
	}

	async recDelete(type: CrudRecordType, docId: string): Promise<void> {
		await runInInjectionContext(this.injector, async () => {
			const firestore = inject(Firestore);
			const path = `${type}_records_log`;
			if (!await this.svcUiDialogs.dialogConfirm('delete')) return;
			if (!path) return

			const docRef = doc(firestore, path, docId);
			await deleteDoc(docRef);
			await this.svcUiNotifications.showNotification('Deleted');
		})
	}

	async recAddCopy<T extends GlobalRecordIntf>(type: CrudRecordType, data: T) {
		const path: FirebasePaths = `${type}_records_log`;
		if (!path) {
			console.error('No path for type', type);
			return;
		}

		await this.recCreate(path, {
			...data,
			id       : null,
			timestamp: Timestamp.now()
		} as T);
		this.svcUiNotifications.showNotification('Record Duplicated');

	}


}
